"use client";
import { StructuredData } from "@/components/common";

import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import {
  ArrowRight,
  ArrowUpRight,
  BadgeCheck,
  Building2,
  Check,
  ClipboardCheck,
  Clapperboard,
  Coins,
  Factory,
  GraduationCap,
  Headset,
  HeartPulse,
  Megaphone,
  MessagesSquare,
  ShieldCheck,
  ShoppingCart,
  Zap,
} from "lucide-react";
import ServiceFaq from "@/components/services/ServiceFaq";
import { CTAButton } from "@/components/ui/cta-button";
import { t } from "@/content/i18n/messages";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";

// Adobe app lockups — existing product iconography, shown as framed media
const heroApps: Array<{ name: string; icon: string }> = [
  { name: "Photoshop", icon: "/images/partners/adobe-icons/photoshop.svg" },
  { name: "Illustrator", icon: "/images/partners/adobe-icons/illustrator.svg" },
  { name: "Premiere", icon: "/images/partners/adobe-icons/premiere.svg" },
  { name: "Express", icon: "/images/partners/adobe-icons/express.svg" },
  { name: "Acrobat Pro", icon: "/images/partners/adobe-icons/acrobat.svg" },
  {
    name: "Creative Cloud",
    icon: "/images/partners/adobe-icons/creative-cloud.svg",
  },
];

const stats: Array<{ value: string; label: string }> = [
  { value: "RM", label: "Quoted in Ringgit" },
  { value: "SST", label: "Treatment Stated" },
  { value: "100%", label: "Genuine Software" },
  { value: "MyInvois", label: "E-Invoice Issued" },
];

const products: Array<{
  icon: string;
  name: string;
  description: string;
  features: string[];
  popular?: boolean;
}> = [
  {
    icon: "/images/partners/adobe-icons/creative-cloud.svg",
    name: "Creative Cloud Pro for Teams",
    description:
      "Create professional content fast with 20+ apps, including Photoshop and Illustrator, plus Adobe Firefly creative AI for images, video and audio.",
    popular: true,
    features: [
      "20+ creative apps including Photoshop, Illustrator, Premiere Pro",
      "Adobe Firefly generative AI features",
      "100GB cloud storage per user",
      "Collaboration & sharing tools",
      "Regular updates & new features",
    ],
  },
  {
    icon: "/images/partners/adobe-icons/photoshop.svg",
    name: "Photoshop",
    description:
      "Create amazing brand images and graphics with the industry-standard app. Comes with Adobe Express and Adobe Firefly generative AI features.",
    features: [
      "Advanced photo manipulation",
      "AI-powered editing with Generative Fill",
      "Adobe Firefly generative AI",
      "Includes Adobe Express",
      "Seamless Creative Cloud integration",
    ],
  },
  {
    icon: "/images/partners/adobe-icons/express.svg",
    name: "Adobe Express",
    description:
      "Quickly and easily make standout brand content from thousands of beautiful templates. Includes Adobe Firefly generative AI features.",
    features: [
      "Thousands of beautiful templates",
      "Adobe Firefly generative AI",
      "Brand kit management",
      "Social media content creation",
      "Easy drag-and-drop editing",
    ],
  },
  {
    icon: "/images/partners/adobe-icons/illustrator.svg",
    name: "Illustrator",
    description:
      "Create beautiful logos, icons, infographics, packaging designs and more — and use them anywhere at any size.",
    features: [
      "Precision vector artwork",
      "Logo and branding design",
      "Infographics and icons",
      "Packaging design tools",
      "Scalable graphics for any size",
    ],
  },
  {
    icon: "/images/partners/adobe-icons/premiere.svg",
    name: "Premiere",
    description:
      "Create social clips, promo spots, explainer videos and more with the leading video editor.",
    features: [
      "Social media video creation",
      "Promo and explainer videos",
      "AI-powered auto-reframe",
      "Motion graphics templates",
      "Professional video editing",
    ],
  },
  {
    icon: "/images/partners/adobe-icons/acrobat.svg",
    name: "Acrobat Pro",
    description:
      "Give your teams the power to create, edit, sign and manage PDFs — quickly, easily, anywhere.",
    features: [
      "Create and edit PDFs",
      "E-signatures and forms",
      "Team collaboration tools",
      "Document security",
      "Mobile PDF editing",
    ],
  },
];

const benefits: Array<{
  title: string;
  description: string;
  icon: LucideIcon;
}> = [
  {
    title: "Authorized Reseller",
    description:
      "Adobe Certified Reseller status means you receive 100% genuine licences with full manufacturer support and warranty.",
    icon: ShieldCheck,
  },
  {
    title: "Quoted in Ringgit",
    description:
      "Priced locally in RM with SST treatment stated and a MyInvois e-invoice issued — no currency surprises and no paperwork left for you to chase.",
    icon: Coins,
  },
  {
    title: "Local Expert Support",
    description:
      "Malaysian-based technical support team available to assist with deployment, troubleshooting, and optimization.",
    icon: Headset,
  },
  {
    title: "Fast Deployment",
    description:
      "Quick license provisioning and deployment assistance. Get your team productive with Adobe tools in days, not weeks.",
    icon: Zap,
  },
  {
    title: "License Management",
    description:
      "Simplified license tracking and renewal management. We help you maintain compliance and optimize your subscriptions.",
    icon: ClipboardCheck,
  },
  {
    title: "Training & Onboarding",
    description:
      "Optional training sessions to help your team maximize productivity with Adobe Creative Cloud applications.",
    icon: GraduationCap,
  },
];

const industries: Array<{
  name: string;
  description: string;
  icon: LucideIcon;
}> = [
  {
    name: "Advertising & Marketing",
    description: "Create compelling campaigns",
    icon: Megaphone,
  },
  {
    name: "Media & Entertainment",
    description: "Produce stunning content",
    icon: Clapperboard,
  },
  {
    name: "E-commerce",
    description: "Design engaging storefronts",
    icon: ShoppingCart,
  },
  {
    name: "Education",
    description: "Empower creative learning",
    icon: GraduationCap,
  },
  {
    name: "Manufacturing",
    description: "Visualize product designs",
    icon: Factory,
  },
  {
    name: "Real Estate",
    description: "Showcase properties beautifully",
    icon: Building2,
  },
  {
    name: "Healthcare",
    description: "Communicate with clarity",
    icon: HeartPulse,
  },
  {
    name: "Professional Services",
    description: "Present data professionally",
    icon: ClipboardCheck,
  },
];

// FAQ items — the matching FAQPage structured data stays co-located in page.tsx
const faqs: Array<{ question: string; answer: string }> = [
  {
    question: "What types of Adobe licenses do you offer?",
    answer:
      "We offer the full range of Adobe licensing options including individual licenses, team licenses, and enterprise agreements (ETLA/VIP). Our team will help you choose the most cost-effective option based on your organization's size and needs.",
  },
  {
    question: "How quickly can licenses be deployed?",
    answer:
      "Timelines depend on the licence type and the agreement behind it — VIP orders and enterprise agreements move at different speeds, and we confirm the timeline with your quotation rather than promising one up front. We also assist with admin console setup and user provisioning.",
  },
  {
    question: "Do you provide training for Adobe products?",
    answer:
      "Yes, we offer optional training and onboarding sessions to help your team get up to speed with Adobe Creative Cloud applications. Training can be customized based on your specific use cases and skill levels.",
  },
  {
    question: "What support do you provide after purchase?",
    answer:
      "We provide ongoing technical support, license management assistance, and renewal reminders. Our local team in Malaysia is available to help with any deployment issues, software updates, or technical questions.",
  },
  {
    question: "Can you help migrate from other creative software?",
    answer:
      "Absolutely. We assist organizations migrating from other creative suites to Adobe. This includes license planning, deployment strategy, and training to ensure a smooth transition for your team.",
  },
];

interface AdobePartnerProps {
  locale?: Locale;
}


// FAQ schema co-located here so /ms and /zh render it too (the [locale] catch-all
// renders only the component; a route-file StructuredData never ships there).
const adobeFaqs = [
  { question: "What types of Adobe licenses do you offer?", answer: "We offer the full range of Adobe licensing options including individual licenses, team licenses, and enterprise agreements (ETLA/VIP). Our team will help you choose the most cost-effective option based on your organization's size and needs." },
  { question: "How quickly can licenses be deployed?", answer: "Timelines depend on the licence type and the agreement behind it — VIP orders and enterprise agreements move at different speeds, and we confirm the timeline with your quotation rather than promising one up front. We also assist with admin console setup and user provisioning." },
  { question: "Do you provide training for Adobe products?", answer: "Yes, we offer optional training and onboarding sessions to help your team get up to speed with Adobe Creative Cloud applications. Training can be customized based on your specific use cases and skill levels." },
  { question: "What support do you provide after purchase?", answer: "We provide ongoing technical support, license management assistance, and renewal reminders. Our local team in Malaysia is available to help with any deployment issues, software updates, or technical questions." },
  { question: "Can you help migrate from other creative software?", answer: "Absolutely. We assist organizations migrating from other creative suites to Adobe. This includes license planning, deployment strategy, and training to ensure a smooth transition for your team." },
];

export default function AdobePartner({
  locale = defaultLocale,
}: AdobePartnerProps) {
  const prefersReducedMotion = useReducedMotion();

  // Hero entrance moves only: opacity stays 1 so the hero paints before
  // hydration (LCP).
  const reveal = (delay: number) => ({
    initial: prefersReducedMotion ? false : { y: 12 },
    animate: { y: 0 },
    transition: { duration: 0.55, delay, ease: [0.21, 0.6, 0.35, 1] as const },
  });

  const inView = {
    initial: prefersReducedMotion ? false : { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.5, ease: [0.21, 0.6, 0.35, 1] as const },
  };

  return (
    <div className="bg-background">
      <StructuredData type="faq" faqs={adobeFaqs} />
      {/* Hero */}
      <section className="bg-background pb-16 pt-20 lg:pt-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-16">
            <div>
              <motion.p
                {...reveal(0)}
                className="mb-5 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted"
              >
                {t(locale, "Partnership")}
              </motion.p>
              <motion.h1
                {...reveal(0.08)}
                className="font-display text-4xl font-bold leading-[1.12] tracking-tight text-neutral-emphasis md:text-5xl"
              >
                {t(locale, "Transform your")}{" "}
                <span className="emphasis-underline">
                  {t(locale, "creative workflow")}
                </span>{" "}
                {t(locale, "with Adobe")}
              </motion.h1>
              <motion.p
                {...reveal(0.16)}
                className="mt-6 max-w-xl text-lg leading-relaxed text-neutral-muted md:text-xl"
              >
                {t(
                  locale,
                  "Genuine Creative Cloud licensing with local support, quoted in Ringgit with SST treatment stated and MyInvois e-invoicing handled.",
                )}
              </motion.p>
              <motion.div
                {...reveal(0.22)}
                className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-neutral-muted"
              >
                {[
                  "100% Genuine Licenses",
                  "VIP Volume Pricing",
                  "Local Support",
                ].map((benefit) => (
                  <span key={benefit} className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-primary" aria-hidden />
                    {t(locale, benefit)}
                  </span>
                ))}
              </motion.div>
              <motion.div {...reveal(0.28)} className="mt-8">
                <span className="inline-flex items-center gap-2.5 rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold text-neutral-emphasis">
                  <BadgeCheck className="h-4 w-4 text-primary" aria-hidden />
                  {t(locale, "Adobe Certified Reseller")}
                </span>
              </motion.div>
              <motion.div
                {...reveal(0.34)}
                className="mt-6 flex flex-wrap items-center gap-4"
              >
                <CTAButton
                  href="/contact/"
                  size="large"
                  className="rounded-full transition"
                >
                  Get Enterprise Quote
                </CTAButton>
                <CTAButton
                  href="#products"
                  size="large"
                  variant="outline"
                  className="rounded-full transition"
                >
                  View Products
                </CTAButton>
              </motion.div>
            </div>

            {/* Adobe product lineup — framed media */}
            <motion.div
              {...reveal(0.2)}
              className="rounded-2xl border border-border bg-card p-8 sm:p-10"
            >
              <img
                src="/images/partners/adobe.png"
                alt={t(locale, "Adobe Creative Cloud")}
                className="mx-auto h-auto w-36 sm:w-40"
              />
              <div className="mt-8 h-px w-full bg-border" aria-hidden />
              <div className="mt-8 grid grid-cols-3 gap-x-4 gap-y-6">
                {heroApps.map(({ name, icon }) => (
                  <div
                    key={name}
                    className="flex flex-col items-center gap-2 text-center"
                  >
                    <img src={icon} alt="" className="h-10 w-10" aria-hidden />
                    <span className="text-xs font-medium text-neutral-muted">
                      {name}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
        <div className="mt-16 h-px w-full bg-border" aria-hidden />
      </section>

      {/* Stats strip */}
      <section className="bg-background py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            {...inView}
            className="grid grid-cols-2 gap-8 lg:grid-cols-4"
          >
            {stats.map(({ value, label }) => (
              <div key={label} className="text-center lg:text-left">
                <p className="font-display text-3xl font-bold text-neutral-emphasis">
                  {value}
                </p>
                <p className="mt-1 text-sm text-neutral-muted">
                  {t(locale, label)}
                </p>
              </div>
            ))}
          </motion.div>
        </div>
        <div className="mt-12 h-px w-full bg-border" aria-hidden />
      </section>

      {/* Products */}
      <section id="products" className="bg-background py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="mb-14 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "Adobe Products")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {t(locale, "Creative tools for")}{" "}
              <span className="emphasis-underline">
                {t(locale, "every need")}
              </span>
            </h2>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-neutral-muted">
              {t(
                locale,
                "From individual apps to complete creative suites, we provide genuine Adobe licenses tailored to your business requirements.",
              )}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {products.map(
              ({ icon, name, description, features, popular }, index) => (
                <motion.div
                  key={name}
                  {...inView}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.06,
                    ease: [0.21, 0.6, 0.35, 1] as const,
                  }}
                  className={
                    popular
                      ? "flex h-full flex-col rounded-2xl border border-primary/40 bg-card p-7"
                      : "flex h-full flex-col rounded-2xl border border-border bg-card p-7 transition-colors duration-200 hover:border-neutral-border"
                  }
                >
                  <div className="mb-5 flex items-start justify-between gap-3">
                    <img src={icon} alt="" className="h-12 w-12" aria-hidden />
                    {popular && (
                      <span className="rounded-full bg-primary/10 px-3 py-1 font-mono text-xs uppercase tracking-wider text-primary">
                        {t(locale, "Most Popular")}
                      </span>
                    )}
                  </div>
                  <h3 className="mb-2 font-display text-lg font-semibold text-neutral-emphasis">
                    {name}
                  </h3>
                  <p className="mb-5 text-sm leading-relaxed text-neutral-muted">
                    {t(locale, description)}
                  </p>
                  <ul className="mb-6 flex-1 space-y-2">
                    {features.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-start gap-2 text-sm text-neutral-muted"
                      >
                        <Check
                          className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary"
                          aria-hidden
                        />
                        <span>{t(locale, feature)}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    href={getLocalizedPathname("/contact/", locale)}
                    className="group inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-all duration-200 hover:gap-2.5"
                  >
                    {t(locale, popular ? "Get Team Quote" : "Get Quote")}
                    <ArrowUpRight className="h-4 w-4" aria-hidden />
                  </Link>
                </motion.div>
              ),
            )}
          </div>

          {/* Custom quote */}
          <motion.div {...inView} className="mt-12">
            <div className="rounded-2xl border border-border bg-card p-8 sm:p-10">
              <div className="flex flex-col items-center gap-6 sm:flex-row sm:gap-8">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10">
                  <MessagesSquare
                    className="h-6 w-6 text-primary"
                    aria-hidden
                  />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="font-display text-lg font-semibold text-neutral-emphasis">
                    {t(locale, "Need a custom package for your team?")}
                  </h3>
                  <p className="mt-1 text-neutral-muted">
                    {t(
                      locale,
                      "We'll create a tailored Adobe solution that fits your specific business requirements and budget.",
                    )}
                  </p>
                </div>
                <CTAButton
                  href="/contact/"
                  size="large"
                  className="w-full flex-shrink-0 whitespace-nowrap rounded-full transition sm:w-auto"
                >
                  Request Custom Quote
                </CTAButton>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="bg-neutral-surface py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="mb-14 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "Why Choose Us")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {t(locale, "Your trusted")}{" "}
              <span className="emphasis-underline">
                {t(locale, "Adobe partner")}
              </span>{" "}
              {t(locale, "in Malaysia")}
            </h2>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-neutral-muted">
              {t(
                locale,
                "Beyond just selling licenses, we provide end-to-end support to ensure your Adobe investment delivers maximum value.",
              )}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {benefits.map(({ title, description, icon: Icon }, index) => (
              <motion.div
                key={title}
                {...inView}
                transition={{
                  duration: 0.5,
                  delay: index * 0.06,
                  ease: [0.21, 0.6, 0.35, 1] as const,
                }}
                className="flex h-full flex-col rounded-2xl border border-border bg-card p-7 transition-colors duration-200 hover:border-neutral-border"
              >
                <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
                  <Icon className="h-5 w-5 text-primary" aria-hidden />
                </div>
                <h3 className="mb-3 font-display text-lg font-semibold text-neutral-emphasis">
                  {t(locale, title)}
                </h3>
                <p className="text-sm leading-relaxed text-neutral-muted">
                  {t(locale, description)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="bg-background py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="mb-14 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "Industries")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {t(locale, "Industries we")}{" "}
              <span className="emphasis-underline">{t(locale, "serve")}</span>
            </h2>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-neutral-muted">
              {t(
                locale,
                "Adobe solutions power creative and business workflows across diverse industries in Malaysia.",
              )}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:gap-5">
            {industries.map(({ name, description, icon: Icon }, index) => (
              <motion.div
                key={name}
                {...inView}
                transition={{
                  duration: 0.5,
                  delay: index * 0.04,
                  ease: [0.21, 0.6, 0.35, 1] as const,
                }}
                className="rounded-2xl border border-border bg-card p-6 text-center transition-colors duration-200 hover:border-neutral-border"
              >
                <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
                  <Icon className="h-5 w-5 text-primary" aria-hidden />
                </div>
                <h3 className="mb-1 text-sm font-semibold text-neutral-emphasis lg:text-base">
                  {t(locale, name)}
                </h3>
                <p className="text-xs text-neutral-muted">
                  {t(locale, description)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ — matches the FAQPage structured data */}
      <section className="bg-neutral-surface py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="mb-12 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "FAQ")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {t(locale, "Common questions about our")}{" "}
              <span className="emphasis-underline">
                {t(locale, "Adobe partnership")}
              </span>
            </h2>
          </motion.div>
          <ServiceFaq items={faqs} />
        </div>
      </section>

      {/* CTA — dark proof moment */}
      <section className="bg-background pb-24 pt-24 lg:pt-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            {...inView}
            className="rounded-3xl bg-hero-background p-10 sm:p-14"
          >
            <div className="max-w-3xl">
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-primary-contrast/60">
                {t(locale, "Start a conversation")}
              </p>
              <h2 className="font-display text-3xl font-bold leading-tight text-primary-contrast md:text-4xl">
                {t(locale, "Ready to transform your creative workflow?")}
              </h2>
              <p className="mt-4 text-lg text-primary-contrast/70">
                {t(
                  locale,
                  "Get in touch with our team for a personalized quote and discover how Adobe Creative Cloud can elevate your business.",
                )}
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <CTAButton
                  href="/contact/"
                  size="large"
                  className="rounded-full transition"
                >
                  Request a quote
                </CTAButton>
                <Link
                  href="#products"
                  className="group inline-flex items-center justify-center gap-2 rounded-full border border-primary-contrast/20 px-8 py-3 text-sm font-semibold text-primary-contrast transition hover:border-primary-contrast/40 hover:bg-primary-contrast/10"
                >
                  {t(locale, "View Adobe products")}
                  <ArrowRight
                    className="h-4 w-4 transition-transform group-hover:translate-x-1"
                    aria-hidden
                  />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
